import { type LawFields, LawFieldsService, type TUser } from '@/types/generated'
import { defineStore } from 'pinia'
import { ref } from 'vue'
import { lawTopicsEN, lawTopicsFR } from '@/i18n/lawTopics'
import { lawFieldsDE, lawFieldsEN, lawFieldsFR } from '@/i18n/lawFields'
import { useI18n } from 'vue-i18n'

const lawFieldsFRReverse: Record<string, string> = Object.fromEntries(
  Object.entries(lawFieldsFR).map(([german, french]) => [french, german])
)

export const useLawFieldStore = defineStore('lawFieldStore', () => {
  const lawFields = ref<LawFields | null>(null)
  const i18n = useI18n()

  const fetchLawFields = async () => {
    lawFields.value = await LawFieldsService.lawFieldsRetrieve()
  }

  const getLawFieldOptions = (user: TUser, locale?: string) => {
    const lawFieldsList = user?.org.law_fields || []
    const currentLocale = locale || i18n.locale.value

    return (lawFieldsList as string[]).map((lawField) => {
      let translatedTitle = lawField

      if (currentLocale === 'fr') {
        translatedTitle = lawFieldsFR[lawField] || lawField
      } else if (currentLocale === 'en') {
        translatedTitle = lawFieldsEN[lawField] || lawField
      } else {
        translatedTitle = lawFieldsDE[lawField] || lawField
      }

      return {
        value: lawField,
        title: translatedTitle
      }
    })
  }

  /**
   * Resolve topics for a law field key, handling cross-jurisdiction lookups.
   * The lawFields data may have German or French keys depending on the org's
   * jurisdiction. The lawField param may be in either format (e.g. call data
   * created under a different jurisdiction). This tries a direct lookup first,
   * then falls back to the translated equivalent.
   *
   * :param lawField: The law field key to look up
   * :return: Object with topics array and whether they matched directly, or undefined
   */
  const resolveTopicsForField = (
    lawField: string
  ): { topics: string[]; directMatch: boolean } | undefined => {
    if (!lawFields.value) return undefined

    const direct = lawFields.value.data[lawField]
    if (direct) return { topics: direct, directMatch: true }

    const frenchEquivalent = lawFieldsFR[lawField]
    if (frenchEquivalent) {
      const viaFrench = lawFields.value.data[frenchEquivalent]
      if (viaFrench) return { topics: viaFrench, directMatch: false }
    }

    const germanEquivalent = lawFieldsFRReverse[lawField]
    if (germanEquivalent) {
      const viaGerman = lawFields.value.data[germanEquivalent]
      if (viaGerman) return { topics: viaGerman, directMatch: false }
    }

    return undefined
  }

  const getLawTopicOptions = (lawField: string, locale?: string) => {
    if (!lawFields.value) return []
    if (!lawField) return []

    const currentLocale = locale || i18n.locale.value

    const resolved = resolveTopicsForField(lawField)
    if (!resolved) return []

    // Only filter German topics to those with French translations when:
    // 1. The locale is French
    // 2. The law field key is German (exists in lawFieldsFR)
    // 3. The topics were found via direct lookup (i.e. they ARE German topics)
    // When topics were resolved cross-jurisdiction, they're already in the
    // target language and should not be filtered.
    const shouldFilterToFrenchTranslations =
      currentLocale === 'fr' && lawFieldsFR[lawField] !== undefined && resolved.directMatch

    const mappedOptions = resolved.topics
      .filter((topic) => {
        if (shouldFilterToFrenchTranslations) {
          return lawTopicsFR[topic] !== undefined
        }
        return true
      })
      .map((topic) => {
        let translatedTitle = topic
        if (currentLocale === 'fr') {
          translatedTitle = lawTopicsFR[topic] || topic
        } else if (currentLocale === 'en') {
          translatedTitle = lawTopicsEN[topic] || topic
        }

        return {
          value: topic,
          title: translatedTitle
        }
      })
      .sort((a, b) => a.title.localeCompare(b.title))

    return mappedOptions
  }

  return {
    lawFields,
    fetchLawFields,
    getLawTopicOptions,
    getLawFieldOptions
  }
})
